import { callMcpTool, listMcpTools } from "./mcpClient";
import { formatToolResult } from "./mcpFormatters";
import { buildModuleDashboard, canBuildDashboard } from "./mcpDashboard";

const pause = (ms = 60) => new Promise((resolve) => setTimeout(resolve, ms));

const COLLECTION_LABELS = {
  purchaseorders: "Purchase Orders (PO)",
  salesorders: "Sales Orders",
  asns: "ASN",
  grns: "Goods Receipt (GRN)",
  shipments: "Shipment Orders",
  items: "Items",
};

function collectionFromOrderId(orderId) {
  if (!orderId) return null;
  const id = orderId.toUpperCase();
  if (/^(PO|PP)\d+/.test(id)) return "purchaseorders";
  if (/^SO\d+/.test(id)) return "salesorders";
  if (/^ASN\d+/.test(id)) return "asns";
  if (/^GRN\d+/.test(id)) return "grns";
  if (/^(SHIP|SHP)\d+/.test(id)) return "shipments";
  return null;
}

function detectCollection(userText) {
  const q = userText.toLowerCase();
  const orderIds = extractOrderIds(userText);
  if (orderIds.length) {
    const fromId = collectionFromOrderId(orderIds[0]);
    if (fromId) return fromId;
  }

  // Prefer document type over generic "sku/items" when both are mentioned.
  if (/\b(grn|goods?\s*receipt)\b/.test(q)) return "grns";
  if (/\b(shipment|shipments|shipping|shipment\s*order)\b/.test(q)) return "shipments";
  if (/\b(po|purchase\s*orders?|procurement|pp)\b/.test(q)) return "purchaseorders";
  if (/\b(asn|advance\s*shipping|ship\s*notice)\b/.test(q)) return "asns";
  if (/\b(sales?\s*orders?|\bso\b)/.test(q)) return "salesorders";
  if (/\b(items?|sku|skus|inventory)\b/.test(q)) return "items";

  return null;
}

function wantsSkuData(userText) {
  return /\b(sku|skus|line\s*items?|item\s*list|which\s+sku)\b/i.test(userText);
}

/** Dashboard UI only when the user explicitly asks for a dashboard. */
function wantsDashboard(userText) {
  const q = String(userText || "").toLowerCase();
  return (
    /\bdashboard\b/.test(q) ||
    /\b(kpi|kpis)\s+(view|dashboard|panel|summary)\b/.test(q) ||
    /\b(visual|live)\s+(dashboard|summary|panel)\b/.test(q)
  );
}

function detectIntent(userText) {
  const q = userText.toLowerCase();

  if (!q || q === "help" || q === "?" || /\b(what can you do|available tools|help me)\b/.test(q)) {
    return "help";
  }
  if (/\b(how many|count|total number|number of)\b/.test(q)) return "count";
  if (/\b(guidance|metadata|schema|fields)\b/.test(q)) return "guidance";
  if (/\b(total|sum|spend|aggregate|analytics|report|value|price|amount)\b/.test(q)) return "find";
  if (/\b(list|show|get|find|search|provide|details?|dashboard|tell|give)\b/.test(q)) return "find";
  if (/\b(PO|PP|SO|ASN|GRN|SHIP|SHP)[-\s#]?\w+/i.test(userText)) return "find";

  return "find";
}

/** Collect every document id mentioned in the user question (supports multi-PO/ASN/...). */
function extractOrderIds(userText) {
  if (!userText) return [];

  const found = [];
  const seen = new Set();
  const pushId = (raw) => {
    if (!raw) return;
    let id = String(raw).toUpperCase().replace(/[-\s]/g, "");
    if (/^\d+$/.test(id) && /\bgrn\b/i.test(userText)) id = `GRN${id}`;
    if (/^\d+$/.test(id) && /\bshipment\b/i.test(userText)) id = `SHIP${id}`;
    if (!/^(PO|PP|SO|ASN|GRN|SHIP|SHP)\d+$/i.test(id) && !/^[A-Z]{1,4}\d{3,}$/i.test(id)) return;
    if (seen.has(id)) return;
    seen.add(id);
    found.push(id);
  };

  // Explicit prefixed ids anywhere in the text (comma / and / space separated).
  const explicit = userText.matchAll(/\b((?:GRN|SHIP|SHP|ASN|SO|PP|PO)\d+)\b/gi);
  for (const match of explicit) pushId(match[1]);

  // Fallback patterns when only one loosely-phrased id exists.
  if (!found.length) {
    const patterns = [
      /\b(?:grn|goods?\s*receipt)\s*(?:number|no\.?|#)?\s*:?\s*((?:GRN)?\d+)\b/i,
      /\b(?:shipment(?:\s*order)?|ship(?:ping)?)\s*(?:number|no\.?|order|#)?\s*:?\s*((?:SHIP|SHP)?\d+)\b/i,
      /\b(?:po|purchase\s*order|pp)\s*(?:number|no\.?|#)?\s*:?\s*([A-Z]{1,3}\d+)\b/i,
      /\b(?:so|sales\s*order)\s*(?:number|no\.?|#)?\s*:?\s*([A-Z]{1,3}\d+)\b/i,
      /\b(?:asn)\s*(?:number|no\.?|#)?\s*:?\s*([A-Z]{1,3}\d+)\b/i,
      /\b([A-Z]{2,4}\d{4,})\b/i,
    ];
    for (const pattern of patterns) {
      const match = userText.match(pattern);
      if (match?.[1]) pushId(match[1]);
    }
  }

  return found;
}

function extractOrderId(userText) {
  const ids = extractOrderIds(userText);
  return ids[0] || null;
}

/** Match IDs that differ only by leading zeros, e.g. SO000000565 → SO00000565 */
function orderIdRegexFilter(field, orderId) {
  const match = String(orderId).match(/^([A-Z]+)0*(\d+)$/i);
  if (!match) return { [field]: orderId };
  const prefix = match[1].toUpperCase();
  const significant = match[2].replace(/^0+/, "") || "0";
  return {
    [field]: { $regex: `^${prefix}0*${significant}$`, $options: "i" },
  };
}

function buildOrderIdFilter(collection, orderId) {
  if (collection === "purchaseorders") {
    return {
      $or: [
        orderIdRegexFilter("purchase_order.orderId", orderId),
        orderIdRegexFilter("orderId", orderId),
      ],
    };
  }
  if (collection === "salesorders") {
    return {
      $or: [
        orderIdRegexFilter("soDetails.soNumber", orderId),
        orderIdRegexFilter("sales_order.orderId", orderId),
        orderIdRegexFilter("salesOrder.orderId", orderId),
      ],
    };
  }
  if (collection === "asns") {
    return {
      $or: [
        orderIdRegexFilter("asnDetails.asnNumber", orderId),
        orderIdRegexFilter("asnNumber", orderId),
      ],
    };
  }
  if (collection === "grns") {
    return {
      $or: [
        orderIdRegexFilter("AsnHeader.GRN Number", orderId),
        orderIdRegexFilter("AsnHeader.grnNumber", orderId),
        orderIdRegexFilter("grnNumber", orderId),
        orderIdRegexFilter("grnDetails.grnNumber", orderId),
      ],
    };
  }
  if (collection === "shipments") {
    return {
      $or: [
        orderIdRegexFilter("shipmentDetails.shipmentNumber", orderId),
        orderIdRegexFilter("shipmentNumber", orderId),
        orderIdRegexFilter("shipmentOrder", orderId),
        orderIdRegexFilter("header.shipmentNumber", orderId),
      ],
    };
  }
  return null;
}

function buildMultiOrderIdFilter(collection, orderIds) {
  const ids = (orderIds || []).filter(Boolean);
  if (!ids.length) return null;
  if (ids.length === 1) return buildOrderIdFilter(collection, ids[0]);

  const clauses = [];
  for (const id of ids) {
    const filter = buildOrderIdFilter(collection, id);
    if (!filter) continue;
    if (Array.isArray(filter.$or)) clauses.push(...filter.$or);
    else clauses.push(filter);
  }
  return clauses.length ? { $or: clauses } : null;
}

function extractEntityTerm(userText) {
  const match = userText.match(/\b(?:for|in|from|by|the)\s+([A-Za-z0-9][A-Za-z0-9\s-]{1,30})\s*$/i);
  return match?.[1]?.trim() || null;
}

function buildFilterQuery(userText, collection) {
  const orderIds = extractOrderIds(userText);
  if (orderIds.length) {
    const idFilter = buildMultiOrderIdFilter(collection, orderIds);
    if (idFilter) return idFilter;
  }

  const entity = extractEntityTerm(userText);
  if (entity) {
    if (collection === "purchaseorders") {
      return {
        $or: [
          { "supplier.supplier": { $regex: entity, $options: "i" } },
          { "purchase_order.warehouse": { $regex: entity, $options: "i" } },
          { "buyer.buyer": { $regex: entity, $options: "i" } },
        ],
      };
    }
    if (collection === "asns") {
      return {
        $or: [
          { "asnDetails.warehouse": { $regex: entity, $options: "i" } },
          { "shipFrom.supplierReference": { $regex: entity, $options: "i" } },
        ],
      };
    }
    if (collection === "salesorders") {
      return {
        $or: [
          { warehouse: { $regex: entity, $options: "i" } },
          { "soDetails.owner": { $regex: entity, $options: "i" } },
          { "destination.destinationName": { $regex: entity, $options: "i" } },
        ],
      };
    }
    if (collection === "grns") {
      return {
        $or: [
          { "AsnHeader.Facility Name": { $regex: entity, $options: "i" } },
          { "AsnHeader.Owner": { $regex: entity, $options: "i" } },
          { facilityName: { $regex: entity, $options: "i" } },
        ],
      };
    }
    if (collection === "shipments") {
      return {
        $or: [
          { "shipmentDetails.source": { $regex: entity, $options: "i" } },
          { "destination.destinationName": { $regex: entity, $options: "i" } },
          { "carrier.carrierName": { $regex: entity, $options: "i" } },
        ],
      };
    }
  }

  return null;
}

function buildQueryPlan(userText) {
  const intent = detectIntent(userText);
  const collection = detectCollection(userText) || "purchaseorders";
  const orderIds = extractOrderIds(userText);
  const findLimit = orderIds.length > 0 ? Math.max(orderIds.length, 10) : 10;

  if (intent === "help") {
    return { tool: "metadata_tool", args: {}, collection: "" };
  }

  if (intent === "guidance") {
    return {
      tool: "analytics_guidance_tool",
      args: { collection_name: collection },
      collection,
    };
  }

  if (intent === "count") {
    return {
      tool: "count_documents_tool",
      args: {
        collection_name: collection,
        filter_query: buildFilterQuery(userText, collection),
      },
      collection,
    };
  }

  // PO / ASN / SO / GRN / Shipment all use Fast MCP find_documents_tool.
  return {
    tool: "find_documents_tool",
    args: {
      collection_name: collection,
      filter_query: buildFilterQuery(userText, collection),
      limit: findLimit,
    },
    collection,
  };
}

function formatHelpText(tools) {
  return [
    "I can help you with partner-portal data via Fast MCP. Ask normally, and I'll answer specifically:",
    "",
    "- How many POs are generated?",
    "- PO number PO00011251 — provide the total price",
    "- What is the status of ASN…?",
    "- GRN / goods receipt details",
    "- Sales order status / shipment tracking",
    "- Which SKUs are on this PO?",
    "",
    "Ask for a **dashboard** when you want the visual summary panel:",
    "- Create dashboard for PO / ASN / GRN / Sales / Shipment",
    "- Dashboard of these PO PO00011251, PP00000021 and PP00000020",
    "",
    `Connected to **${tools.length}** MCP data tools.`,
  ].join("\n");
}

async function streamAnswer(content, onProgress) {
  const chunkSize = 28;
  let acc = "";

  for (let i = 0; i < content.length; i += chunkSize) {
    acc += content.slice(i, i + chunkSize);
    if (onProgress) await onProgress({ text: acc });
    await pause(10);
  }
}

function parseDocsFromToolResult(result) {
  const text = result?.content?.[0]?.text;
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function orderIdFromDoc(doc, collection) {
  if (!doc || typeof doc !== "object") return null;
  if (collection === "purchaseorders") {
    return doc.purchase_order?.orderId || doc.orderId || null;
  }
  if (collection === "salesorders") {
    return doc.soDetails?.soNumber || doc.sales_order?.orderId || doc.salesOrder?.orderId || null;
  }
  if (collection === "asns") {
    return doc.asnDetails?.asnNumber || doc.asnNumber || null;
  }
  if (collection === "grns") {
    return (
      doc.AsnHeader?.["GRN Number"] ||
      doc.AsnHeader?.grnNumber ||
      doc.grnDetails?.grnNumber ||
      doc.grnNumber ||
      null
    );
  }
  if (collection === "shipments") {
    return (
      doc.shipmentDetails?.shipmentNumber ||
      doc.header?.shipmentNumber ||
      doc.shipmentNumber ||
      doc.shipmentOrder ||
      null
    );
  }
  return null;
}

function buildItemsFilter(collection, orderId) {
  if (!orderId) return null;
  if (collection === "purchaseorders") {
    return {
      $or: [{ "detail.purchaseOrder": orderId }, { purchaseOrder: orderId }],
    };
  }
  if (collection === "salesorders") {
    return {
      $or: [{ "detail.salesOrder": orderId }, { salesOrder: orderId }],
    };
  }
  if (collection === "asns") {
    return {
      $or: [{ "detail.asnNumber": orderId }, { asnNumber: orderId }],
    };
  }
  if (collection === "grns") {
    return {
      $or: [
        { "detail.grnNumber": orderId },
        { grnNumber: orderId },
        { "detail.GRN Number": orderId },
      ],
    };
  }
  if (collection === "shipments") {
    return {
      $or: [
        { "detail.shipmentNumber": orderId },
        { shipmentNumber: orderId },
        { "detail.shipmentOrder": orderId },
      ],
    };
  }
  return null;
}

/**
 * Attach SKU / line items from the items collection when the header doc lacks them.
 * Runs for dashboard modules and whenever the user asks for SKUs.
 */
async function enrichWithSkuItems(userText, collection, result, signal, { force = false } = {}) {
  if (!force && !wantsSkuData(userText) && !canBuildDashboard(collection)) return result;

  const docs = parseDocsFromToolResult(result);
  if (!docs.length) return result;

  let changed = false;
  const enriched = [];

  for (const doc of docs.slice(0, 15)) {
    const hasLineItems =
      (Array.isArray(doc.lineItems) && doc.lineItems.length > 0) ||
      (Array.isArray(doc.AsnDetail) && doc.AsnDetail.length > 0) ||
      (Array.isArray(doc.stops) && doc.stops.some((s) => Array.isArray(s?.lineItems) && s.lineItems.length));

    if (hasLineItems) {
      enriched.push(doc);
      continue;
    }

    const orderId = extractOrderId(userText) || orderIdFromDoc(doc, collection);
    const filter = buildItemsFilter(collection, orderId);
    if (!filter) {
      enriched.push(doc);
      continue;
    }

    try {
      const itemsResult = await callMcpTool(
        "find_documents_tool",
        { collection_name: "items", filter_query: filter, limit: 50 },
        signal
      );
      const items = parseDocsFromToolResult(itemsResult);
      if (items.length) {
        doc.lineItems = items.map((item) => {
          const detail = item.detail || {};
          return {
            skuId: detail.skuId || item.skuId,
            description: detail.description || item.description,
            orderedQty: detail.orderedQty,
            openQty: detail.openQty,
            receivedQty: detail.receivedQty || detail.totalReceivedQty,
            uom: detail.uom,
            unitPrice: detail.unitPrice,
            detail,
          };
        });
        changed = true;
      }
    } catch (_) {
      /* keep header-only doc if items lookup fails */
    }
    enriched.push(doc);
  }

  if (!changed) return result;

  return {
    ...result,
    content: [{ type: "text", text: JSON.stringify(enriched) }],
    isError: false,
  };
}

async function fetchDocumentsByOrderIds(collection, orderIds, signal) {
  const docs = [];
  const seen = new Set();

  for (const orderId of orderIds) {
    if (signal?.aborted) throw Object.assign(new Error("Aborted"), { name: "AbortError" });
    const filter = buildOrderIdFilter(collection, orderId);
    if (!filter) continue;

    const result = await callMcpTool(
      "find_documents_tool",
      { collection_name: collection, filter_query: filter, limit: 5 },
      signal
    );
    for (const doc of parseDocsFromToolResult(result)) {
      const key =
        orderIdFromDoc(doc, collection)?.toUpperCase() ||
        JSON.stringify(doc).slice(0, 80);
      if (seen.has(key)) continue;
      seen.add(key);
      docs.push(doc);
    }
  }

  return {
    content: [{ type: "text", text: JSON.stringify(docs) }],
    isError: false,
  };
}

/**
 * @param {(update: { text: string, dashboard?: object|null }) => void|Promise<void>} onProgress
 * @returns {Promise<{ text: string, dashboard: object|null }>}
 */
export async function answerWithMcp(userText, signal, onProgress) {
  const tools = await listMcpTools(signal);
  const plan = buildQueryPlan(userText);
  const orderIds = extractOrderIds(userText);

  if (plan.tool === "metadata_tool" && detectIntent(userText) === "help") {
    const helpText = formatHelpText(tools);
    await streamAnswer(helpText, onProgress);
    return { text: helpText, dashboard: null };
  }

  let result;
  const showDashboard = wantsDashboard(userText);
  // Only pull SKU line-items when needed (dashboard / explicit SKU ask).
  const needSkuEnrich = showDashboard || wantsSkuData(userText);

  if (plan.tool === "find_documents_tool" && orderIds.length > 1) {
    // Fetch each requested id separately so multi-id answers / dashboards include all records.
    result = await fetchDocumentsByOrderIds(plan.collection, orderIds, signal);
    if (needSkuEnrich) {
      result = await enrichWithSkuItems(userText, plan.collection, result, signal, { force: true });
    }
  } else {
    result = await callMcpTool(plan.tool, plan.args, signal);
    if (plan.tool === "find_documents_tool" && needSkuEnrich) {
      result = await enrichWithSkuItems(userText, plan.collection, result, signal, { force: true });
    } else if (plan.tool === "find_documents_tool") {
      result = await enrichWithSkuItems(userText, plan.collection, result, signal);
    }
  }

  const docs = parseDocsFromToolResult(result);
  // Dashboard UI only when user explicitly asks for a dashboard.
  const dashboard =
    showDashboard && !result?.isError && canBuildDashboard(plan.collection) && docs.length
      ? buildModuleDashboard(docs, plan.collection)
      : null;

  // When a visual dashboard is shown, keep the text brief; details live in the panel.
  let answer;
  if (dashboard) {
    const noun = COLLECTION_LABELS[plan.collection] || "records";
    const count = dashboard?.kpis?.[0]?.value || docs.length;
    answer =
      Number(count) > 1
        ? `Here's the **${noun}** multi-order dashboard for **${count}** records, with aggregated KPIs and SKU line items.`
        : `Here's the **${noun}** dashboard with summary, KPIs, and SKU line items.`;
  } else {
    const formatted = formatToolResult(plan.tool, result, plan.collection, userText);
    const title =
      plan.collection && COLLECTION_LABELS[plan.collection]
        ? `## ${COLLECTION_LABELS[plan.collection]}\n\n`
        : "";
    answer = `${title}${formatted}`;
  }

  await streamAnswer(answer, onProgress);
  if (onProgress && dashboard) {
    await onProgress({ text: answer, dashboard });
  }
  return { text: answer, dashboard };
}
